/*
 * Lab results tabs (<lab-results-tabs>, used by sections/lab-results-tabs.liquid).
 *
 * Purpose: a horizontal row of filter-chip tabs switches the pollutant panel (chart, note, results).
 * All panels share one grid cell (see assets/lab-results-tabs.css), so switching never changes the section height.
 * When the tab row overflows (mobile), it scrolls horizontally: a fade and a chevron show while more tabs
 * are hidden on the right, the chevron scrolls the row, and the selected tab is always scrolled into view.
 *
 * Config (read from the markup, no options):
 *   [data-lab-tabs]      wrapper of the tab row; gets [data-more] while tabs are hidden on the right
 *   [data-lab-tablist]   the scrollable role="tablist"
 *   [role="tab"]         tab buttons (also carry the theme editor block attributes);
 *                        the one rendered with aria-selected="true" is selected on load
 *   [data-lab-panel]     panels, one per tab, in the same order; the active one has .is-active
 *   [data-lab-more]      chevron button that scrolls the tab row
 *
 * Usage:
 *   <script src="{{ 'lab-results-tabs.js' | asset_url }}" defer></script>
 *   <lab-results-tabs> …tab row, panels… </lab-results-tabs>
 * Works with several instances per page and re-initialises when the theme editor reloads the section.
 */

if (!customElements.get('lab-results-tabs')) {
  /** Share of the visible tab row scrolled by one chevron click. */
  const MORE_SCROLL_RATIO = 0.6;

  class LabResultsTabs extends HTMLElement {
    /**
     * Collects the parts and binds the listeners.
     * @returns {void}
     */
    connectedCallback() {
      this.tabsWrapper = this.querySelector('[data-lab-tabs]');
      this.tabList = this.querySelector('[data-lab-tablist]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[data-lab-panel]'));
      this.moreButton = this.querySelector('[data-lab-more]');

      if (!this.tabList || this.tabs.length === 0) {
        return;
      }

      const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

      this.index = Math.max(0, selectedIndex);
      this.overflowFrame = null;

      this.onTabClick = this.onTabClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMoreClick = this.onMoreClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.requestOverflowUpdate = this.requestOverflowUpdate.bind(this);

      this.tabList.addEventListener('click', this.onTabClick);
      this.tabList.addEventListener('keydown', this.onKeydown);
      this.tabList.addEventListener('scroll', this.requestOverflowUpdate, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.moreButton) {
        this.moreButton.addEventListener('click', this.onMoreClick);
      }

      this.resizeObserver = new ResizeObserver(this.requestOverflowUpdate);
      this.resizeObserver.observe(this.tabList);

      this.scrollTabIntoView(this.index, 'instant');
      this.updateOverflow();
    }

    /**
     * Removes the listeners (section removed or re-rendered in the theme editor).
     * @returns {void}
     */
    disconnectedCallback() {
      if (!this.tabList) {
        return;
      }

      this.tabList.removeEventListener('click', this.onTabClick);
      this.tabList.removeEventListener('keydown', this.onKeydown);
      this.tabList.removeEventListener('scroll', this.requestOverflowUpdate);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);

      if (this.moreButton) {
        this.moreButton.removeEventListener('click', this.onMoreClick);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      cancelAnimationFrame(this.overflowFrame);
    }

    /**
     * Activates a tab and its panel.
     * @param {number} index - Index of the tab to activate.
     * @param {{ focus?: boolean }} [options] - Move focus to the tab.
     * @returns {void}
     */
    select(index, options = {}) {
      if (index < 0 || index >= this.tabs.length) {
        return;
      }

      this.index = index;

      this.tabs.forEach((tab, i) => {
        const isActive = i === index;

        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
      });

      if (options.focus) {
        this.tabs[index].focus({ preventScroll: true });
      }

      this.scrollTabIntoView(index);
    }

    /**
     * Scrolls the tab row (never the page) so the given tab is fully visible.
     * Smooth scrolling and its reduced-motion fallback come from CSS `scroll-behavior`.
     * @param {number} index - Tab index.
     * @param {ScrollBehavior} [behavior] - Override, e.g. 'instant' for the initially selected tab.
     * @returns {void}
     */
    scrollTabIntoView(index, behavior) {
      const tab = this.tabs[index];

      if (!tab) {
        return;
      }

      const listRect = this.tabList.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(this.tabList).paddingLeft) || 0;
      let delta = 0;

      if (tabRect.left < listRect.left + padding) {
        delta = tabRect.left - listRect.left - padding;
      } else if (tabRect.right > listRect.right - padding) {
        delta = tabRect.right - listRect.right + padding;
      }

      if (delta === 0) {
        return;
      }

      const options = { left: this.tabList.scrollLeft + delta };

      if (behavior) {
        options.behavior = behavior;
      }

      this.tabList.scrollTo(options);
    }

    /**
     * Batches overflow updates to one per animation frame (scroll and resize fire often).
     * @returns {void}
     */
    requestOverflowUpdate() {
      cancelAnimationFrame(this.overflowFrame);

      this.overflowFrame = requestAnimationFrame(() => this.updateOverflow());
    }

    /**
     * Shows the fade and the chevron while tabs are hidden past the right edge of the row.
     * @returns {void}
     */
    updateOverflow() {
      if (!this.tabsWrapper) {
        return;
      }

      const { scrollLeft, scrollWidth, clientWidth } = this.tabList;
      const hasMore = Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1;

      this.tabsWrapper.toggleAttribute('data-more', hasMore);
    }

    /**
     * Click on a tab button (delegated from the tab list).
     * @param {MouseEvent} event - Click event.
     * @returns {void}
     */
    onTabClick(event) {
      const tab = event.target.closest('[role="tab"]');

      if (!tab) {
        return;
      }

      this.select(this.tabs.indexOf(tab));
    }

    /**
     * Arrow / Home / End keyboard navigation between tabs (WAI-ARIA tabs pattern, automatic activation).
     * @param {KeyboardEvent} event - Keydown event.
     * @returns {void}
     */
    onKeydown(event) {
      const lastIndex = this.tabs.length - 1;
      let nextIndex = null;

      if (event.key === 'ArrowRight') {
        nextIndex = this.index === lastIndex ? 0 : this.index + 1;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = this.index === 0 ? lastIndex : this.index - 1;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = lastIndex;
      }

      if (nextIndex === null) {
        return;
      }

      event.preventDefault();

      this.select(nextIndex, { focus: true });
    }

    /**
     * Chevron click: scrolls the tab row to reveal the next tabs.
     * @returns {void}
     */
    onMoreClick() {
      this.tabList.scrollBy({ left: this.tabList.clientWidth * MORE_SCROLL_RATIO });
    }

    /**
     * Theme editor: show the tab whose block was selected in the sidebar.
     * @param {CustomEvent} event - shopify:block:select event.
     * @returns {void}
     */
    onBlockSelect(event) {
      const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;

      if (!tab || !this.contains(tab)) {
        return;
      }

      this.select(this.tabs.indexOf(tab));
    }
  }

  customElements.define('lab-results-tabs', LabResultsTabs);
}
